/**
 * Networking drills — the question banks, as pure functions.
 *
 * Here rather than in the page for three reasons. They are testable in
 * milliseconds; the marking uses the same arithmetic the rest of the system
 * does, so a drill cannot teach something the application would contradict; and
 * they work in a van with no signal.
 *
 * **Still no external trivia API, deliberately.** A trivia service would mean a
 * key to keep out of the repository, a hole in the content-security policy, a
 * bill, and questions about film stars rather than fibre.
 *
 * Everything here is drawn from work a GoSpeedNet technician actually does:
 * whether a link will come up given the loss on it, how many customers can hang
 * off a splitter, why a customer can reach an IP address but not a website. All
 * three cost an afternoon when they are got wrong on site, and all three go
 * rusty between jobs.
 */
/** Crypto-grade randomness would be pointless; readable is the priority. */
const pick = (list) => list[Math.floor(Math.random() * list.length)];
const between = (low, high) => low + Math.floor(Math.random() * (high - low + 1));
const squash = (value) => value.trim().toLowerCase().replace(/\s+/g, '');
/** Accepts "3.5", "3.5db", "3.5 dB" — the unit is noise, the number is not. */
const numberish = (given) => {
    const match = /-?\d+(\.\d+)?/.exec(given.replace(/,/g, ''));
    return match ? Number(match[0]) : null;
};
const within = (given, target, tolerance = 0.051) => {
    const value = numberish(given);
    return value !== null && Math.abs(value - target) <= tolerance;
};
/* ── Optical power budget ───────────────────────────────────────────────────
 *
 * The most useful arithmetic on this list. A technician standing at a FAT with
 * a light meter needs to know whether the number in front of them means "this
 * will work", "this will work until it rains" or "something is wrong upstream".
 */
/** Typical losses, in dB. Conservative values — the ones worth planning with. */
const LOSS = {
    splicePerJoint: 0.1,
    connectorPerPair: 0.5,
    fibrePerKm: 0.35,
    splitter: { 2: 3.5, 4: 7.0, 8: 10.5, 16: 13.5, 32: 17.0, 64: 20.5 },
};
/**
 * Total loss down a path, in dB.
 *
 * Splitter loss dominates everything else and is not linear in the split — each
 * doubling costs roughly 3 dB plus a little, which is why a 1:64 PON is a
 * different engineering problem from a 1:8.
 */
export function pathLoss(input) {
    const total = input.km * LOSS.fibrePerKm +
        input.splices * LOSS.splicePerJoint +
        input.connectorPairs * LOSS.connectorPerPair +
        (input.split ? LOSS.splitter[input.split] : 0);
    // Two decimals: a light meter reads to 0.01 dB and pretending to more
    // precision than the instrument is how a budget becomes a fiction.
    return Math.round(total * 100) / 100;
}
/**
 * Whether a link will come up, given the loss and the receiver's sensitivity.
 *
 * The 3 dB margin is the whole point of the function. A link built to exactly
 * its budget works on the day and fails in the first heavy rain, after the
 * first repair splice, and as the transmitter ages.
 */
export function linkVerdict(lossDb, txDbm, rxSensitivityDbm, marginDb = 3) {
    const received = Math.round((txDbm - lossDb) * 100) / 100;
    const headroom = Math.round((received - rxSensitivityDbm) * 100) / 100;
    if (headroom < 0) {
        return {
            received,
            ok: false,
            marginal: false,
            detail: `${received} dBm is below the receiver's ${rxSensitivityDbm} dBm. The link will not come up.`,
        };
    }
    if (headroom < marginDb) {
        return {
            received,
            ok: true,
            marginal: true,
            detail: `${received} dBm leaves only ${headroom} dB of headroom. It will work today and fail in the rain.`,
        };
    }
    return {
        received,
        ok: true,
        marginal: false,
        detail: `${received} dBm, ${headroom} dB of headroom. Sound.`,
    };
}
export function opticalDrill() {
    const km = between(1, 12) / 2;
    const splices = between(2, 10);
    const connectorPairs = between(1, 3);
    const split = pick([null, 4, 8, 16, 32]);
    const loss = pathLoss({ km, splices, connectorPairs, split });
    const describe = [
        `${km} km of fibre`,
        `${splices} splices`,
        `${connectorPairs} connector pair${connectorPairs === 1 ? '' : 's'}`,
        split ? `a 1:${split} splitter` : null,
    ]
        .filter(Boolean)
        .join(', ');
    const kind = pick(['loss', 'verdict']);
    if (kind === 'loss') {
        return {
            prompt: `A path has ${describe}. What is the total loss, in dB?`,
            hint: '0.35 dB/km, 0.1 dB a splice, 0.5 dB a connector pair.',
            answer: `${loss} dB`,
            accepts: (given) => within(given, loss, 0.35),
            because: `${km} × 0.35 = ${Math.round(km * 0.35 * 100) / 100}, ` +
                `+ ${splices} × 0.1 = ${Math.round(splices * 0.1 * 100) / 100}, ` +
                `+ ${connectorPairs} × 0.5 = ${connectorPairs * 0.5}` +
                (split ? `, + ${LOSS.splitter[split]} for the 1:${split} splitter` : '') +
                `. Total ${loss} dB.`,
        };
    }
    const tx = pick([3, 5, 7]);
    const sensitivity = pick([-24, -27, -28]);
    const verdict = linkVerdict(loss, tx, sensitivity);
    return {
        prompt: `Transmitting at ${tx} dBm through ${describe}, into a receiver rated ` +
            `${sensitivity} dBm. What arrives, in dBm?`,
        hint: 'Work out the loss first, then subtract it from the transmit power.',
        answer: `${verdict.received} dBm`,
        accepts: (given) => within(given, verdict.received, 0.35),
        because: `${tx} − ${loss} = ${verdict.received} dBm. ${verdict.detail}`,
    };
}
/* ── PON planning ─────────────────────────────────────────────────────────── */
export function ponDrill() {
    const kind = pick(['homes', 'ports', 'loss']);
    if (kind === 'homes') {
        const split = pick([8, 16, 32, 64]);
        const ports = between(2, 8);
        const homes = split * ports;
        return {
            prompt: `An OLT port feeds ${ports} × 1:${split} splitters. How many homes can it serve?`,
            answer: String(homes),
            accepts: (given) => squash(given) === String(homes),
            because: `${ports} × ${split} = ${homes}. Whether you *should* put that many on one port is a contention question, not an arithmetic one.`,
        };
    }
    if (kind === 'ports') {
        const homes = pick([120, 250, 400, 500, 800]);
        const split = pick([16, 32, 64]);
        const needed = Math.ceil(homes / split);
        return {
            prompt: `You need to reach ${homes} homes using 1:${split} splitters. How many OLT ports at minimum?`,
            hint: 'Round up — you cannot have most of a port.',
            answer: String(needed),
            accepts: (given) => squash(given) === String(needed),
            because: `${homes} ÷ ${split} = ${(homes / split).toFixed(2)}, so ${needed} ports.`,
        };
    }
    const split = pick([4, 8, 16, 32, 64]);
    const loss = LOSS.splitter[split];
    return {
        prompt: `Roughly how much loss does a 1:${split} splitter add, in dB?`,
        hint: 'Each doubling costs about 3 dB, plus a little.',
        answer: `${loss} dB`,
        accepts: (given) => within(given, loss, 1.5),
        because: `About ${loss} dB. A 1:2 costs ~3.5 dB and every doubling adds ~3 more, which is why 1:64 at ~20.5 dB is a different problem from 1:8.`,
    };
}
/* ── DNS and DHCP ─────────────────────────────────────────────────────────
 *
 * The support calls. "The internet is down" is a DNS problem far more often
 * than it is a fibre problem, and being able to tell the two apart on the phone
 * saves a van.
 */
const DNS_DHCP = [
    {
        prompt: 'A customer can ping 8.8.8.8 but no website loads. What is broken?',
        answer: 'DNS',
        accept: ['dns', 'dnsresolution', 'nameresolution', 'theirdns'],
        because: 'Packets are reaching the internet, so routing is fine. What is failing is turning names into addresses — check the resolver the router hands out.',
    },
    {
        prompt: 'Which DHCP message does a client send first when it has no lease?',
        answer: 'DHCPDISCOVER',
        accept: ['discover', 'dhcpdiscover'],
        because: 'DISCOVER, OFFER, REQUEST, ACK — in that order. A client with an expired lease it remembers may skip to REQUEST.',
    },
    {
        prompt: 'What address does a Windows machine give itself when DHCP fails?',
        answer: '169.254.x.x (APIPA)',
        accept: ['169.254', 'apipa', 'linklocal', '169.254.0.0/16'],
        because: 'A 169.254 address is the machine saying "nobody answered". On a customer site it almost always means the router is not handing out leases, not that the line is down.',
    },
    {
        prompt: 'What does a DNS TTL of 3600 mean in practice?',
        answer: 'Cached for an hour',
        accept: ['1hour', 'anhour', '3600seconds', '60minutes', 'onehour'],
        because: 'Resolvers may keep the answer for an hour. It is why a DNS change you made this morning is still not visible to some customers — and why you lower the TTL *before* a migration, not during it.',
    },
    {
        prompt: 'A customer changed their Wi-Fi password and now no device connects, though the box lights are normal. Is this a network fault?',
        answer: 'No',
        accept: ['no', 'notafault', 'notanetworkfault', 'clientside'],
        because: 'Normal lights mean the line and the ONT are fine. This is CLIENT_ERROR_WIFI_PASSWORD — the most common "outage" there is, and it is fixed on the phone.',
    },
    {
        prompt: 'Which record type points a name at another name?',
        answer: 'CNAME',
        accept: ['cname'],
        because: 'CNAME aliases one name to another. An A record points at an address; you cannot put a CNAME at a zone apex.',
    },
    {
        prompt: 'What is the usual MTU on an Ethernet link, in bytes?',
        answer: '1500',
        accept: ['1500', '1500bytes'],
        because: '1500. It matters because PPPoE takes 8 bytes for its header, so a PPPoE link is 1492 — and a mismatch shows up as large pages hanging while small ones load.',
    },
    {
        prompt: 'A customer 40 km away complains of lag in games but speed tests are fine. What is the likely cause?',
        answer: 'Latency, not bandwidth',
        accept: ['latency', 'ping', 'rtt', 'jitter', 'latencynotbandwidth'],
        because: 'Games need low, steady latency and very little bandwidth. A perfect speed test tells you nothing about jitter — which is what actually ruins a game.',
    },
];
export function dnsDrill() {
    const question = pick(DNS_DHCP);
    return {
        prompt: question.prompt,
        answer: question.answer,
        accepts: (given) => {
            const value = squash(given);
            return question.accept.some((option) => value === option || value.includes(option));
        },
        because: question.because,
    };
}
/* ── General networking trivia ────────────────────────────────────────────── */
const GENERAL = [
    {
        prompt: 'Which OSI layer does a switch work at?',
        answer: 'Layer 2',
        accept: ['2', 'layer2', 'l2', 'datalink', 'two'],
        because: 'Layer 2, on MAC addresses. A router is layer 3, on IP addresses — which is why a switch cannot get you to another subnet.',
    },
    {
        prompt: 'What does PON stand for?',
        answer: 'Passive Optical Network',
        accept: ['passiveopticalnetwork', 'passiveoptical'],
        because: '"Passive" is the important word: the splitter needs no power, which is why a PON keeps working when the street loses electricity.',
    },
    {
        prompt: 'How many usable host addresses in a /30?',
        answer: '2',
        accept: ['2', 'two'],
        because: 'Four addresses, minus network and broadcast, leaves 2. It is the classic point-to-point link size.',
    },
    {
        prompt: 'What does an ONT do?',
        answer: 'Converts light to Ethernet',
        accept: ['opticaltoethernet', 'lighttoethernet', 'converts', 'terminatesthefibre', 'opticalnetworkterminal'],
        because: 'It terminates the fibre at the customer and hands off Ethernet. It is the boundary between our network and their equipment — and the boundary matters when apportioning a fault.',
    },
    {
        prompt: 'Which is more sensitive to a dirty connector: the transmit side or the receive side?',
        answer: 'Receive',
        accept: ['receive', 'rx', 'receiveside', 'thereceiveside'],
        because: 'The receiver is working with the weakest signal on the path, so it has the least margin to lose. Clean both, but a dirty RX connector is what kills a link.',
    },
    {
        prompt: 'What is the first thing to check when a whole street is down?',
        answer: 'Upstream — the FAT or the POP',
        accept: ['upstream', 'fat', 'pop', 'thefat', 'thepop', 'olt', 'power'],
        because: 'A single customer is usually their own equipment; a whole street is ours. Work down the tree from the POP rather than driving to the first address that called.',
    },
    {
        prompt: 'Why does a 100 Mbps line not make WhatsApp messages arrive faster?',
        answer: 'They are latency-bound, not bandwidth-bound',
        accept: ['latency', 'toosmall', 'notbandwidth', 'latencybound', 'messagesaretiny'],
        because: 'A message is a few hundred bytes. Past a few Mbps, extra bandwidth changes nothing you can perceive — which is the single most useful thing to be able to explain to a customer being upsold elsewhere.',
    },
    {
        prompt: 'What does a solid red LOS light on an ONT mean?',
        answer: 'Loss of signal — no light arriving',
        accept: ['lossofsignal', 'nolight', 'fibrecut', 'nosignal', 'brokenfibre'],
        because: 'No light at all. Either the fibre is cut, the connector is out, or something upstream is dark. It is not a configuration problem, so do not spend time on the router.',
    },
];
export function generalDrill() {
    const question = pick(GENERAL);
    return {
        prompt: question.prompt,
        answer: question.answer,
        accepts: (given) => {
            const value = squash(given);
            return question.accept.some((option) => value === option || value.includes(option));
        },
        because: question.because,
    };
}
/** Every bank, for a test that wants to sweep them. */
export const DRILL_BANKS = {
    optical: opticalDrill,
    pon: ponDrill,
    dns: dnsDrill,
    general: generalDrill,
};
//# sourceMappingURL=trivia.js.map